import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sharedStyles } from '../styles/shared-styles';
import { logger, loggerEventBus, type UserLogEntry, type LogScope, type LogLevel } from '../../core/Logger';

const LEVEL_OPTIONS: Array<{ value: LogLevel | '*' | 'off'; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: '*', label: '*' },
  { value: 'debug', label: 'debug+' },
  { value: 'log', label: 'log+' },
  { value: 'info', label: 'info+' },
  { value: 'warn', label: 'warn+' },
  { value: 'error', label: 'error' },
];

const SCOPE_EMOJIS: Record<LogScope, string> = {
  core: '⚙️',
  physics: '🔵',
  animation: '🎬',
  character: '🎮',
  player: '👤',
  npc: '🤖',
  camera: '🎥',
  spline: '📍',
  level: '🗺️',
  assets: '📦',
  ui: '🎨',
  triggers: '🎯',
  input: '⌨️',
  performance: '⚡',
  debug: '🐛',
  combat: '⚔️',
  environment: '🌍',
  behavior: '🔄',
  user: '❗',
};

/**
 * Console Panel
 * 
 * Features:
 * - Display logger output with color coding by level
 * - Auto-scroll to bottom on new entries
 * - Clear console button
 * - Timestamps for each entry
 * - Collapsible logger filter configuration
 */
@customElement('console-panel')
export class ConsolePanel extends LitElement {
  static styles = [
    sharedStyles,
    css`
    :host {
      display: block;
      height: 100%;
      color: #eee;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 11px;
    }

    .panel-content {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .console-header {
      padding: 6px 12px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .console-title {
      font-weight: 600;
      color: #eee;
    }

    .header-spacer {
      flex: 1;
    }

    .header-btn {
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 3px 8px;
      color: #ccc;
      cursor: pointer;
      font-size: 10px;
    }

    .header-btn:hover {
      background: rgba(255, 255, 255, 0.2);
      color: #fff;
    }

    .header-btn.active {
      background: rgba(100, 200, 255, 0.2);
      border-color: rgba(100, 200, 255, 0.4);
      color: #fff;
    }

    /* Filter Settings Section */
    .filter-section {
      background: rgba(0, 0, 0, 0.2);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding: 8px 12px;
      flex-shrink: 0;
    }

    .filter-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .filter-input {
      flex: 1;
      background: rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 4px;
      padding: 4px 8px;
      color: #fff;
      font-family: inherit;
      font-size: 11px;
    }

    .filter-input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .filter-input:focus {
      outline: none;
      border-color: rgba(100, 200, 255, 0.5);
    }

    .apply-btn {
      background: rgba(100, 200, 255, 0.2);
      border: 1px solid rgba(100, 200, 255, 0.4);
      border-radius: 4px;
      padding: 4px 10px;
      color: #fff;
      cursor: pointer;
      font-size: 10px;
    }

    .apply-btn:hover {
      background: rgba(100, 200, 255, 0.3);
    }

    .presets-row {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: 8px;
    }

    .preset-btn {
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 3px;
      padding: 2px 6px;
      color: #aaa;
      cursor: pointer;
      font-size: 9px;
    }

    .preset-btn:hover {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }

    .preset-btn.reset {
      background: rgba(100, 200, 255, 0.15);
      border-color: rgba(100, 200, 255, 0.3);
      color: #8cf;
    }

    .preset-btn.reset:hover {
      background: rgba(100, 200, 255, 0.25);
      color: #fff;
    }

    .scope-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .scope-chip {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 6px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
      font-size: 10px;
    }

    .scope-chip.enabled {
      background: rgba(100, 200, 255, 0.15);
    }

    .scope-emoji {
      font-size: 10px;
    }

    .scope-select {
      background: transparent;
      border: none;
      color: #ccc;
      font-size: 9px;
      cursor: pointer;
      padding: 0;
    }

    .scope-select:focus {
      outline: none;
    }

    .scope-chip.enabled .scope-select {
      color: #fff;
    }

    /* Console Entries */
    .console-entries {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
    }

    .console-entry {
      padding: 2px 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.03);
      display: flex;
      gap: 8px;
      font-size: 11px;
    }

    .console-time {
      opacity: 0.5;
      min-width: 55px;
    }

    .console-level {
      min-width: 40px;
      font-weight: 600;
    }

    .console-message {
      flex: 1;
      word-break: break-word;
    }

    .console-entry.error .console-level { color: #f44336; }
    .console-entry.warn .console-level { color: #ff9800; }
    .console-entry.info .console-level { color: #2196f3; }
    .console-entry.debug .console-level { color: #9e9e9e; }

    .console-entry.error .console-message { color: #ffcdd2; }
    .console-entry.warn .console-message { color: #ffe0b2; }
    .console-entry.info .console-message { color: #bbdefb; }
    .console-entry.debug .console-message { color: #ccc; }

    .console-empty {
      padding: 20px;
      text-align: center;
      opacity: 0.5;
    }

    .filter-hint {
      color: rgba(255, 255, 255, 0.4);
      font-size: 9px;
      margin-bottom: 6px;
    }
  `];

  @state() private consoleLogs: UserLogEntry[] = [];
  @state() private showFilters = false;
  @state() private filterString = '';
  @state() private scopeFilters: Map<LogScope, LogLevel> = new Map();

  private loggerUserCallback?: (entry: UserLogEntry) => void;
  private loggerClearCallback?: () => void;
  private configChangedCallback?: () => void;
  private entriesContainer?: HTMLElement;

  connectedCallback(): void {
    super.connectedCallback();

    // Load existing log entries
    this.consoleLogs = logger.getUserBuffer();
    this.loadCurrentConfig();

    // Subscribe to new log entries
    this.loggerUserCallback = (entry: UserLogEntry) => {
      this.consoleLogs = [...this.consoleLogs, entry];
      this.requestUpdate();
      queueMicrotask(() => this.scrollToBottom());
    };

    this.loggerClearCallback = () => {
      this.consoleLogs = [];
    };

    this.configChangedCallback = () => {
      this.loadCurrentConfig();
    };

    loggerEventBus.on('logger:user', this.loggerUserCallback);
    loggerEventBus.on('logger:clear', this.loggerClearCallback);
    loggerEventBus.on('logger:configChanged', this.configChangedCallback);
  }

  disconnectedCallback(): void {
    if (this.loggerUserCallback) {
      loggerEventBus.off('logger:user', this.loggerUserCallback);
    }
    if (this.loggerClearCallback) {
      loggerEventBus.off('logger:clear', this.loggerClearCallback);
    }
    if (this.configChangedCallback) {
      loggerEventBus.off('logger:configChanged', this.configChangedCallback);
    }
    super.disconnectedCallback();
  }

  protected firstUpdated(): void {
    this.scrollToBottom();
  }

  private loadCurrentConfig(): void {
    const enabled = logger.getEnabled();
    this.scopeFilters = new Map(enabled.map(e => [e.scope, e.minLevel]));
    this.filterString = logger.getFilterString();
  }

  private scrollToBottom(): void {
    if (this.entriesContainer) {
      this.entriesContainer.scrollTop = this.entriesContainer.scrollHeight;
    }
  }

  private clearConsole(): void {
    logger.clearUserBuffer();
    this.consoleLogs = [];
  }

  private toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  private handleFilterInput(e: Event): void {
    this.filterString = (e.target as HTMLInputElement).value;
  }

  private applyFilterString(): void {
    logger.applyFilterString(this.filterString);
  }

  private handleFilterKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      this.applyFilterString();
    }
  }

  private handleScopeChange(scope: LogScope, value: string): void {
    if (value === 'off') {
      logger.setFilter(scope, null);
    } else {
      const level = value === '*' ? 'debug' : value as LogLevel;
      logger.setFilter(scope, level);
    }
  }

  private applyPreset(preset: string): void {
    logger.applyFilterString(preset);
  }

  private resetToDefault(): void {
    logger.resetToDefault();
  }

  private getScopeValue(scope: LogScope): string {
    const level = this.scopeFilters.get(scope);
    if (!level) return 'off';
    return level === 'debug' ? '*' : level;
  }

  private formatConsoleTime(timestamp: number): string {
    const d = new Date(timestamp);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
  }

  private renderFilterSection() {
    const allScopes = logger.getAllScopes();

    return html`
      <div class="filter-section">
        <div class="filter-hint">Filter: scope,level — Use * for all levels (e.g., physics,*;combat,warn)</div>
        
        <div class="filter-row">
          <input
            type="text"
            class="filter-input"
            placeholder="physics,*;combat,warn"
            .value=${this.filterString}
            @input=${this.handleFilterInput}
            @keydown=${this.handleFilterKeydown}
          />
          <button class="apply-btn" @click=${this.applyFilterString}>Apply</button>
        </div>

        <div class="presets-row">
          <button class="preset-btn" @click=${() => this.applyPreset('physics,*')}>physics</button>
          <button class="preset-btn" @click=${() => this.applyPreset('combat,*')}>combat</button>
          <button class="preset-btn" @click=${() => this.applyPreset('animation,*')}>animation</button>
          <button class="preset-btn" @click=${() => this.applyPreset('level,*')}>level</button>
          <button class="preset-btn" @click=${() => this.applyPreset('camera,*')}>camera</button>
          <button class="preset-btn" @click=${() => this.applyPreset('')}>clear all</button>
          <button class="preset-btn reset" @click=${this.resetToDefault}>⟲ Reset</button>
        </div>

        <div class="scope-grid">
          ${allScopes.map(scope => {
            const value = this.getScopeValue(scope);
            const isEnabled = value !== 'off';
            return html`
              <div class="scope-chip ${isEnabled ? 'enabled' : ''}">
                <span class="scope-emoji">${SCOPE_EMOJIS[scope]}</span>
                <select
                  class="scope-select"
                  .value=${value}
                  @change=${(e: Event) => this.handleScopeChange(scope, (e.target as HTMLSelectElement).value)}
                >
                  ${LEVEL_OPTIONS.map(opt => html`
                    <option value=${opt.value} ?selected=${value === opt.value}>${opt.value === 'off' ? scope : opt.label}</option>
                  `)}
                </select>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="panel-content">
        <div class="console-header">
          <span class="console-title">Console</span>
          <span class="header-spacer"></span>
          <button 
            class="header-btn ${this.showFilters ? 'active' : ''}" 
            @click=${this.toggleFilters}
            title="Toggle log filters"
          >⚙ Filters</button>
          <button class="header-btn" @click=${this.clearConsole}>Clear</button>
        </div>

        ${this.showFilters ? this.renderFilterSection() : ''}

        <div
          class="console-entries"
          ${(el: Element) => {
            this.entriesContainer = el as HTMLElement;
          }}
        >
          ${this.consoleLogs.length
            ? this.consoleLogs.map((entry) => {
                return html`
                  <div class="console-entry ${entry.level}">
                    <span class="console-time">${this.formatConsoleTime(entry.timestamp)}</span>
                    <span class="console-level">${entry.level.toUpperCase()}</span>
                    <span class="console-message">${entry.message}</span>
                  </div>
                `;
              })
            : html`<div class="console-empty">No log entries yet. Use logger.userInfo/userWarn/userError to log here.</div>`}
        </div>
      </div>
    `;
  }
}
